import React from 'react'
import Calculator from '@/components/Calculator'
import {Building2, CheckCircle} from 'lucide-react'

export default function CommercialRoofingPage() {
  const benefits = [
    'Reduced maintenance costs over 20+ year lifespan',
    'Energy-efficient cool roof technology',
    'Fast installation minimizes business disruption',
    'Waterproofing excellence for flat and low-slope roofs',
    'Cost-effective compared to traditional built-up roofing',
  ]

  return (
    <div className="bg-gray-50">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-slate-800 to-slate-900 text-white py-12 sm:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8">
            <Building2 className="text-orange-500 mx-auto mb-4" size={56} />
            <h1 className="text-4xl sm:text-5xl font-bold mb-4">
              Flat & Commercial Roof Replacement Estimates
            </h1>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              Instant commercial roof replacement cost calculator for flat roofs, TPO, EPDM rubber roofing, and low-slope systems. Built for business owners and property managers.
            </p>
          </div>

          <div className="flex justify-center">
            <Calculator materialType="commercial" title="Commercial Roof Cost Calculator" />
          </div>
        </div>
      </section>

      {/* Commercial Systems Overview */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-8">
            Flat Roof Estimate by System Type
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="bg-gray-50 p-6 rounded-xl border-2 border-gray-200">
              <h3 className="font-bold text-2xl text-gray-900 mb-3">TPO Roofing</h3>
              <p className="text-orange-600 font-bold text-xl mb-4">$5.00 - $9.00 / sq ft</p>
              <p className="text-gray-600 mb-4">
                Thermoplastic Polyolefin (TPO) is the most popular single-ply commercial roofing membrane. Energy-efficient white surface reflects heat.
              </p>
              <p className="text-sm text-gray-500">Lifespan: 20-30 years</p>
            </div>

            <div className="bg-gray-50 p-6 rounded-xl border-2 border-gray-200">
              <h3 className="font-bold text-2xl text-gray-900 mb-3">EPDM Rubber Roof</h3>
              <p className="text-orange-600 font-bold text-xl mb-4">$4.50 - $8.00 / sq ft</p>
              <p className="text-gray-600 mb-4">
                Ethylene Propylene Diene Monomer (EPDM) rubber roofing. Cost-effective, durable, and easy to repair. Black membrane absorbs heat.
              </p>
              <p className="text-sm text-gray-500">Lifespan: 20-25 years</p>
            </div>

            <div className="bg-gray-50 p-6 rounded-xl border-2 border-gray-200">
              <h3 className="font-bold text-2xl text-gray-900 mb-3">Built-Up Roofing (BUR)</h3>
              <p className="text-orange-600 font-bold text-xl mb-4">$4.00 - $8.00 / sq ft</p>
              <p className="text-gray-600 mb-4">
                Traditional tar and gravel system with multiple layers. Proven track record for flat commercial applications.
              </p>
              <p className="text-sm text-gray-500">Lifespan: 15-30 years</p>
            </div>
          </div>
        </div>
      </section>

      {/* TPO Calculator Details */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-6">
                TPO Roofing Cost Calculator
              </h2>
              <p className="text-lg text-gray-600 mb-6 leading-relaxed">
                TPO roofing systems are the gold standard for commercial flat roofs. Our rubber roof cost calculator provides accurate estimates for material and installation.
              </p>

              <div className="space-y-4">
                {benefits.map((benefit, index) => (
                  <div key={index} className="flex items-start">
                    <CheckCircle className="text-orange-500 mr-3 flex-shrink-0 mt-1" size={20} />
                    <p className="text-gray-700">{benefit}</p>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-white p-8 rounded-xl shadow-xl">
              <h3 className="font-bold text-2xl text-gray-900 mb-4">Commercial Project Sizing</h3>
              <div className="space-y-4">
                <div className="border-l-4 border-blue-800 pl-4">
                  <p className="font-semibold text-gray-900">Small Commercial (5,000 sq ft)</p>
                  <p className="text-orange-600 font-bold text-xl">$25,000 - $45,000</p>
                </div>
                <div className="border-l-4 border-blue-800 pl-4">
                  <p className="font-semibold text-gray-900">Medium Commercial (10,000 sq ft)</p>
                  <p className="text-orange-600 font-bold text-xl">$50,000 - $90,000</p>
                </div>
                <div className="border-l-4 border-blue-800 pl-4">
                  <p className="font-semibold text-gray-900">Large Commercial (20,000 sq ft)</p>
                  <p className="text-orange-600 font-bold text-xl">$100,000 - $180,000</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Metal Estimate Per Square Foot */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 text-center mb-12">
            Metal Roof Estimate Per Square Foot
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="bg-blue-50 p-8 rounded-xl">
              <h3 className="font-bold text-2xl text-gray-900 mb-4">Standing Seam Metal Calculator</h3>
              <p className="text-gray-700 mb-4">
                Standing seam metal roofing features concealed fasteners and vertical leg panels. Premium choice for modern architectural designs.
              </p>
              <p className="text-orange-600 font-bold text-3xl">$10 - $16 per sq ft</p>
            </div>

            <div className="bg-orange-50 p-8 rounded-xl">
              <h3 className="font-bold text-2xl text-gray-900 mb-4">Copper Roof Cost Calculator</h3>
              <p className="text-gray-700 mb-4">
                Copper roofing is the ultimate luxury material. Develops distinctive patina over time. Exceptional 100+ year lifespan.
              </p>
              <p className="text-orange-600 font-bold text-3xl">$25 - $40 per sq ft</p>
            </div>
          </div>
        </div>
      </section>

    </div>
  )
}
